import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, MapPin, Loader2, Eye, Check, SkipForward, Bookmark, Ban, Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { PageHeader, StatusBadge, ConfidenceBadge, ScoreBar, TrackIcon, Empty } from "@/components/Bits";
import MapPicker from "@/components/MapPicker";
import LeadSheet from "@/components/LeadSheet";

const PROVINCES = ["Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador", "Nova Scotia", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan", "Northwest Territories", "Nunavut", "Yukon"];
const eligible = (l) => ["high", "medium"].includes(l.confidence) && ["new", "saved", "approved", "reeligible"].includes(l.status);

function Filters({ tracks, f, set, onRun, running }) {
  const track = tracks.find((t) => t.id === f.track_id);
  const niche = track?.niches.find((n) => n.key === f.niche);
  const [geoQ, setGeoQ] = useState("");
  const showMap = track && track.map_mode !== "none" && niche?.source !== "youtube";
  const locate = async () => {
    const q = geoQ || [f.city, f.province, f.country].filter(Boolean).join(", ");
    try {
      const { data } = await api.get("/geo/geocode", { params: { q } });
      if (!data.length) return toast.error("Location not found");
      set({ lat: data[0].lat, lon: data[0].lon });
      toast.success(data[0].label.slice(0, 80));
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="kh-card min-w-0 space-y-5 p-4 sm:p-6" data-testid="search-filters">
      <Tabs value={f.track_id} onValueChange={(v) => { const t = tracks.find((x) => x.id === v); set({ track_id: v, niche: t.niches[0]?.key }); }}>
        <TabsList className="h-auto flex-wrap bg-secondary/60">
          {tracks.filter((t) => t.active !== false).map((t) => (
            <TabsTrigger key={t.id} value={t.id} data-testid={`track-tab-${t.slug}`} className="gap-2 px-4 py-2">
              <TrackIcon name={t.icon} color={t.color} /> {t.name}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {track && <p className="text-sm text-muted-foreground">{track.description} <span className="font-mono text-xs">· mode: {track.discovery_mode}</span></p>}
      <div className="grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-[1fr_1.3fr]">
        <div className="min-w-0 space-y-4">
          <div>
            <div className="kh-label mb-2">Niche / prospect type</div>
            <Select value={f.niche} onValueChange={(v) => set({ niche: v })}>
              <SelectTrigger data-testid="niche-select"><SelectValue placeholder="Choose niche" /></SelectTrigger>
              <SelectContent>{track?.niches.map((n) => <SelectItem key={n.key} value={n.key} data-testid={`niche-option-${n.key}`}>{n.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="kh-label mb-2">Country</div>
              <Select value={f.country} onValueChange={(v) => set({ country: v, province: "" })}>
                <SelectTrigger data-testid="country-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Canada">Canada</SelectItem><SelectItem value="United States">United States</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <div className="kh-label mb-2">Province / state</div>
              {f.country === "Canada" ? (
                <Select value={f.province} onValueChange={(v) => set({ province: v })}>
                  <SelectTrigger data-testid="province-select"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent>{PROVINCES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              ) : <Input value={f.province} onChange={(e) => set({ province: e.target.value })} placeholder="State" data-testid="province-input" />}
            </div>
          </div>
          <div>
            <div className="kh-label mb-2">City</div>
            <div className="flex gap-2">
              <Input value={f.city} onChange={(e) => set({ city: e.target.value })} placeholder="e.g. Mississauga" data-testid="city-input" />
              <Button variant="secondary" onClick={locate} data-testid="locate-city-btn"><MapPin className="h-4 w-4" /></Button>
            </div>
          </div>
          <div>
            <div className="kh-label mb-2">Keyword filter (optional)</div>
            <Input value={f.keyword} onChange={(e) => set({ keyword: e.target.value })} placeholder={niche?.source === "youtube" ? "e.g. science, history" : "name contains…"} data-testid="keyword-input" />
          </div>
          <div>
            <div className="kh-label mb-2">Seed URLs (optional, one per line)</div>
            <Textarea rows={3} value={f.seeds} onChange={(e) => set({ seeds: e.target.value })} placeholder="https://example-agency.ca" data-testid="seed-urls-input" />
          </div>
        </div>
        <div className="space-y-4">
          {showMap ? (
            <>
              <div className="flex items-center justify-between">
                <div className="kh-label">Target pin & radius {track.map_mode === "primary" ? "· required" : "· optional"}</div>
                <div className="flex gap-2">
                  <Input className="h-8 w-44 text-xs" placeholder="Search map…" value={geoQ} onChange={(e) => setGeoQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && locate()} data-testid="map-search-input" />
                </div>
              </div>
              <MapPicker pin={f.lat != null ? { lat: f.lat, lon: f.lon } : null} radiusKm={f.radius_km} onPick={(p) => set(p)} />
              <div className="flex items-center gap-4">
                <span className="kh-label w-20">Radius</span>
                <Slider value={[f.radius_km]} min={1} max={50} step={1} onValueChange={([v]) => set({ radius_km: v })} data-testid="radius-slider" />
                <span className="w-14 font-mono text-sm" data-testid="radius-value">{f.radius_km} km</span>
              </div>
              <div className="font-mono text-xs text-muted-foreground" data-testid="pin-coords">{f.lat != null ? `${f.lat.toFixed(4)}, ${f.lon.toFixed(4)}` : "Click the map to drop a pin"}</div>
            </>
          ) : (
            <div className="rounded-xl border border-dashed p-6 text-sm text-muted-foreground">
              Source-first discovery: YouTube channels are found via the YouTube Data API (add a key in Settings › Sources), plus any seed URLs you paste.
            </div>
          )}
          <div className="flex items-center gap-3">
            <span className="kh-label">Max results</span>
            <Select value={String(f.limit)} onValueChange={(v) => set({ limit: Number(v) })}>
              <SelectTrigger className="w-24" data-testid="limit-select"><SelectValue /></SelectTrigger>
              <SelectContent>{[10, 20, 30, 40].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
            </Select>
            <Button className="ml-auto rounded-full px-6" onClick={onRun} disabled={running} data-testid="run-search-btn">
              {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}{running ? "Researching…" : "Run manual search"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LeadRow({ l, checked, onCheck, onView, onAction }) {
  return (
    <tr className="border-b transition-colors hover:bg-accent/40" data-testid={`lead-row-${l.id}`}>
      <td className="px-3 py-3"><Checkbox checked={checked} onCheckedChange={onCheck} disabled={!eligible(l)} data-testid={`lead-select-${l.id}`} /></td>
      <td className="px-3 py-3">
        <div className="font-medium">{l.name}</div>
        <a href={l.website || l.source_url} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-primary">{(l.website || l.source_url || "").replace(/^https?:\/\/(www\.)?/, "").slice(0, 36)}</a>
      </td>
      <td className="px-3 py-3 text-sm">{l.contact_name || <span className="text-muted-foreground">—</span>}</td>
      <td className="px-3 py-3">
        <div className="max-w-[180px] truncate text-sm">{l.email || <span className="text-muted-foreground">not found</span>}</div>
        <ConfidenceBadge level={l.confidence} testid={`lead-confidence-${l.id}`} />
      </td>
      <td className="px-3 py-3"><ScoreBar score={l.score} /></td>
      <td className="max-w-[220px] px-3 py-3 text-xs text-muted-foreground">{l.reason}</td>
      <td className="px-3 py-3"><StatusBadge status={l.status} testid={`lead-status-${l.id}`} /></td>
      <td className="px-3 py-3">
        <div className="flex gap-0.5">
          {[["view", Eye, "View"], ["approve", Check, "Approve for draft"], ["save", Bookmark, "Save for later"], ["skip", SkipForward, "Skip"], ["blacklist", Ban, "Blacklist"]].map(([a, I, t]) => (
            <button key={a} title={t} data-testid={`lead-${a}-${l.id}`} onClick={() => (a === "view" ? onView(l) : onAction(l, a))}
              className={`rounded-md p-1.5 transition-colors hover:bg-secondary ${a === "blacklist" ? "hover:text-red-700" : a === "approve" ? "hover:text-primary" : ""}`}>
              <I className="h-4 w-4" strokeWidth={1.75} />
            </button>
          ))}
        </div>
      </td>
    </tr>
  );
}

export default function Prospects() {
  const nav = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [f, setF] = useState(() => JSON.parse(sessionStorage.getItem("kh_search") || "null") || { country: "Canada", province: "Ontario", city: "", lat: 43.6532, lon: -79.3832, radius_km: 10, keyword: "", seeds: "", limit: 20 });
  useEffect(() => { sessionStorage.setItem("kh_search", JSON.stringify(f)); }, [f]);
  const [view, setView] = useState("queue");
  const [leads, setLeads] = useState([]);
  const [run, setRun] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [running, setRunning] = useState(false);
  const [open, setOpen] = useState(null);
  const [aiOn, setAiOn] = useState(false);
  const [aiTip, setAiTip] = useState("");
  const [olOn, setOlOn] = useState(false);
  const set = (p) => setF((x) => ({ ...x, ...p }));

  useEffect(() => {
    api.get("/tracks").then((r) => { setTracks(r.data); setF((x) => (x.track_id && r.data.some((t) => t.id === x.track_id) ? x : { ...x, track_id: r.data[0]?.id, niche: r.data[0]?.niches[0]?.key })); });
    api.get("/settings").then((r) => setAiOn(r.data.ai.enabled));
    api.get("/outlook/status").then((r) => setOlOn(r.data.connected));
  }, []);

  const loadQueue = async () => {
    if (!f.track_id) return;
    const status = view === "queue" ? "new,saved,approved,reeligible" : view === "all" ? "" : view;
    const { data } = await api.get("/leads", { params: { track_id: f.track_id, status, run_id: view === "run" ? run?.id : "" } });
    setLeads(data);
  };
  useEffect(() => { if (view !== "run") loadQueue(); setSel(new Set()); }, [f.track_id, view]); // eslint-disable-line react-hooks/exhaustive-deps

  const runSearch = async () => {
    setRunning(true);
    try {
      const body = { ...f, seed_urls: f.seeds.split("\n").map((s) => s.trim()).filter(Boolean) };
      const { data } = await api.post("/search", body);
      setRun(data.run); setLeads(data.results); setView("run"); setSel(new Set());
      const r = data.run;
      toast.success(`Found ${r.candidates} · audited ${r.audited} · qualified ${r.qualified} · ${r.duplicates} duplicates skipped`);
      r.notes.forEach((n) => toast.warning(n));
    } catch (e) { toast.error(errMsg(e)); } finally { setRunning(false); }
  };

  const updateLead = (l) => { setLeads((xs) => xs.map((x) => (x.id === l.id ? { ...x, ...l } : x))); if (open?.id === l.id) setOpen({ ...open, ...l }); };
  const act = async (l, action) => {
    try {
      const { data } = await api.post(`/leads/${l.id}/action`, { action });
      updateLead(data);
      toast.success(`${l.name}: ${data.status}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const ranked = useMemo(() => [...leads].sort((a, b) => b.score - a.score), [leads]);
  const selectTop = (n) => setSel(new Set(ranked.filter((l) => eligible(l) && l.qualified).slice(0, n).map((l) => l.id)));
  const createDrafts = async () => {
    try {
      const { data } = await api.post("/drafts/bulk", { lead_ids: [...sel] });
      window.dispatchEvent(new Event("kh-quota"));
      if (data.created.length) toast.success(`${data.created.length} drafts created (${data.used_today}/${data.cap} today)`);
      data.skipped.forEach((s) => toast.warning(`${s.name}: ${s.reason}`));
      setSel(new Set());
      const capped = new Set(data.skipped.filter((s) => s.reason.startsWith("Daily cap")).map((s) => s.id));
      view === "run" ? setLeads((xs) => xs.map((x) => (data.created.some((d) => d.lead_id === x.id) ? { ...x, status: "drafted" } : capped.has(x.id) && x.status === "new" ? { ...x, status: "approved" } : x))) : loadQueue();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const pushAll = async () => {
    try {
      const { data } = await api.post("/drafts/push-all");
      toast.success(`${data.pushed} drafts pushed to Outlook`);
      data.errors.slice(0, 2).forEach((x) => toast.error(x.error));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const prioritize = async () => {
    try { const { data } = await api.post("/ai/prioritize", { lead_ids: ranked.slice(0, 15).map((l) => l.id) }); setAiTip(data.suggestion); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <>
      <PageHeader eyebrow="Research · manual only" title="Prospects">
        <Button variant="outline" onClick={() => nav("/drafts")} data-testid="go-draft-queue-btn">Draft queue</Button>
      </PageHeader>
      <Filters tracks={tracks} f={f} set={set} onRun={runSearch} running={running} />

      <div className="kh-card" data-testid="results-panel">
        <div className="flex flex-wrap items-center gap-2 border-b p-4">
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="bg-secondary/60">
              {run && <TabsTrigger value="run" data-testid="view-run-tab">This run</TabsTrigger>}
              <TabsTrigger value="queue" data-testid="view-queue-tab">Review queue</TabsTrigger>
              <TabsTrigger value="saved" data-testid="view-saved-tab">Saved</TabsTrigger>
              <TabsTrigger value="all" data-testid="view-all-tab">All leads</TabsTrigger>
            </TabsList>
          </Tabs>
          <span className="text-xs text-muted-foreground" data-testid="results-count">{leads.length} leads · {sel.size} selected</span>
          <div className="ml-auto flex flex-wrap gap-2">
            {aiOn && <Button size="sm" variant="ghost" onClick={prioritize} data-testid="ai-prioritize-btn"><Sparkles className="mr-1 h-3.5 w-3.5" />AI priority</Button>}
            <Button size="sm" variant="secondary" onClick={() => selectTop(5)} data-testid="select-top-5-btn">Select top 5</Button>
            <Button size="sm" variant="secondary" onClick={() => selectTop(10)} data-testid="select-top-10-btn">Select top 10</Button>
            <Button size="sm" onClick={createDrafts} disabled={!sel.size} data-testid="create-drafts-btn">Create drafts for selected</Button>
            <Button size="sm" variant="outline" onClick={pushAll} disabled={!olOn} title={olOn ? "" : "Connect Outlook in Settings first"} data-testid="push-outlook-btn"><Send className="mr-1 h-3.5 w-3.5" />Push approved drafts to Outlook</Button>
          </div>
        </div>
        {aiTip && <div className="whitespace-pre-wrap border-b bg-primary/5 p-4 text-sm" data-testid="ai-priority-output"><div className="kh-label mb-1">AI suggestion (advisory)</div>{aiTip}</div>}
        {ranked.length === 0 ? <div className="p-6"><Empty testid="results-empty">{view === "run" && run ? (run.candidates ? `All ${run.candidates} candidates were duplicates, suppressed or unreachable — try a new area, niche or wider radius.` : `No candidates found${run.notes?.length ? ` (${run.notes[0]})` : ""}. Try a wider radius or different niche.`) : "No leads here yet. Pick a niche and location, then run a manual search."}</Empty></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead><tr className="border-b text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="w-8 px-3 py-3"></th><th className="px-3 py-3">Company / source</th><th className="px-3 py-3">Contact</th><th className="px-3 py-3">Business email</th>
                <th className="px-3 py-3">Score</th><th className="px-3 py-3">Why qualified</th><th className="px-3 py-3">Status</th><th className="px-3 py-3">Actions</th>
              </tr></thead>
              <tbody>
                {ranked.map((l) => (
                  <LeadRow key={l.id} l={l} checked={sel.has(l.id)} onView={setOpen} onAction={act}
                    onCheck={(c) => setSel((s) => { const n = new Set(s); c ? n.add(l.id) : n.delete(l.id); return n; })} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <LeadSheet lead={open} aiOn={aiOn} onClose={() => setOpen(null)} onAction={act} onChange={updateLead} />
    </>
  );
}
